import { Divider, Stack } from 'component-library';
import { FC, HTMLAttributes, SVGProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { IUCNRedListCategory, Kingdom } from '@/api/rest/resources/types/species';
import { Dot } from '@/components/Charts/components/Dot';
import {
  ChartTooltip,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { colorPalette } from '@/theme/colorPalette';

import { Bar, BarChart, Chart, TooltipProps } from './components/Chart';
import { ChartLegendBar, LegendButton, LegendItemProps } from './components/LegendBar';
import { ChartLegendBarItem } from './components/types';
import { DefaultChartProps } from './types';

const CHART_COLORS = {
  [Kingdom.Animalia]: '#1B8C8C',
  [Kingdom.Plantae]: '#8CA62D',
  [Kingdom.Fungi]: '#AEDFF2',
  [Kingdom.Protista]: '#82B8D9',
  [Kingdom.Monera]: '#376B8C',
} as const;

const SHARED_BAR_PROPS = {
  stackId: 'IUCNCategory',
  isAnimationActive: false,
  barSize: 21,
  stroke: '#fff',
  strokeWidth: 1,
};

export const ThreatenedSpeciesChart: FC<
  HTMLAttributes<HTMLDivElement> & DefaultChartProps & { data: PlotReportFactElement[] }
> = ({ data, ...delegated }) => {
  const { t } = useTranslation();
  const [selectedKingdoms, setSelectedKingdoms] = useState<Kingdom[]>([]);

  const chartConfig = useGetChartElementsConfig();
  const chartData = useChartData(data);
  const kingdomBars = getKingdomGroupBars(data);

  const filteredKingdomBars = kingdomBars.filter((item) =>
    selectedKingdoms.length > 0 ? selectedKingdoms.includes(item.dataKey) : true,
  );
  const filteredChartData =
    selectedKingdoms.length === 0
      ? chartData
      : chartData.reduce<ChartDataEntry[]>((acc, entry) => {
          const hasSelectedKingdom = Object.keys(entry).some((key) => {
            return selectedKingdoms.includes(key as Kingdom) && typeof entry[key as Kingdom] === 'number';
          });

          if (!hasSelectedKingdom) return acc;

          return [...acc, entry];
        }, []);

  const xAxisProps = {
    dataKey: 'iucnLabel',
    type: 'category' as const,
  };

  const domain = [
    0,
    Math.max(
      ...filteredChartData
        .map((entry) => Object.values(entry))
        .flatMap((x) => x)
        .filter((v): v is number => typeof v === 'number'),
    ),
  ];

  return (
    <>
      <Chart.Container {...delegated} config={chartConfig} data={filteredChartData}>
        <BarChart data={filteredChartData} margin={{ bottom: 80 }}>
          {filteredKingdomBars.map((item) => (
            <Bar
              key={item.dataKey}
              {...item}
              activeBar={(props: unknown) => (
                <CustomActiveBar {...(props as ActiveBarProps)} selectedKingdoms={selectedKingdoms} />
              )}
              {...SHARED_BAR_PROPS}
            />
          ))}
          <Chart.XAxis
            {...xAxisProps}
            className='threatened-species-x-axis'
            interval={0}
            tick={(props: unknown) => <CustomTick {...(props as CustomTickProps)} />}
          />
          <Chart.YAxis
            domain={domain}
            allowDecimals={false}
            tickFormatter={(tick) => (Number.isInteger(tick) ? tick : '')}
            className='threatened-species-y-axis'
          >
            <Chart.Label>{t('shared.ncaDetail.details.threatenedSpecies.graph.yAxisTitle')}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip content={<CustomTooltip />} cursor={false} />
        </BarChart>
      </Chart.Container>
      <CustomLegendBar
        kingdoms={kingdomBars.map((item) => item.dataKey)}
        selectedKingdoms={selectedKingdoms}
        setSelectedKingdoms={setSelectedKingdoms}
      />
    </>
  );
};

type CustomTickProps = SVGProps<SVGElement> & { payload: { value: string } };

const CustomTick = ({ x, y, payload }: CustomTickProps) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={10}
        y={-5}
        textAnchor='end'
        transform='rotate(-45)'
        style={{ fontSize: '12px', fill: 'rgba(0, 0, 0, 0.60)' }}
      >
        {payload.value}
      </text>
    </g>
  );
};

const useGetChartElementsConfig = () => {
  const { t } = useTranslation();

  return Object.fromEntries(
    Object.keys(Kingdom).map((kingdomGroup) => [
      kingdomGroup,
      {
        label: t(`global.kingdom.${kingdomGroup as Kingdom}`),
        color: CHART_COLORS[kingdomGroup as Kingdom],
      },
    ]),
  );
};

type ChartDataEntry = {
  [K in Kingdom]?: number;
} & { iucnLabel: string };

const useChartData = (data: PlotReportFactElement[]) => {
  const { t } = useTranslation();

  return Object.values(IUCNRedListCategory).map((iucnCategory) => {
    const iucnCategoryData = data.filter((entry) =>
      entry.labels?.some(
        (label) => label.name === PlotReportFactLabelNameEnum.iucn_redlist_category && label.value === iucnCategory,
      ),
    );

    const kingdomsData = iucnCategoryData.reduce(
      (acc, cur) => {
        const kingdom = cur.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.kingdom)
          ?.value as Kingdom;

        if (!kingdom) return acc;

        const newValue = (acc[`${kingdom}`] || 0) + parseInt(cur.value as string, 10);

        if (newValue === 0) return acc;

        acc[`${kingdom}`] = newValue;
        return acc;
      },
      {} as { [K in Kingdom]?: number },
    );

    return {
      iucnLabel: t(`global.iucn.description.${iucnCategory as IUCNRedListCategory}`),
      ...kingdomsData,
    };
  });
};

const getKingdomGroupBars = (data: PlotReportFactElement[]) => {
  const kingdoms = new Set(
    data.flatMap((item) => {
      if (!item.value) {
        return [];
      }

      const dataLabel = item.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.kingdom)?.value as
        | Kingdom
        | undefined;

      if (!dataLabel) {
        return [];
      }

      return dataLabel;
    }),
  );

  return Array.from(kingdoms).map((kingdom) => {
    return {
      dataKey: kingdom,
      fill: CHART_COLORS[`${kingdom}`],
      className: `threatened-species-${kingdom}`,
    };
  });
};

type ActiveBarProps = SVGProps<SVGElement> & {
  payload: ChartDataEntry;
  value: number[];
  dataKey: string;
  background: { y: number };
  selectedKingdoms: Kingdom[];
};

const CustomActiveBar = ({
  x,
  y,
  width,
  height,
  payload,
  value,
  dataKey,
  background,
  selectedKingdoms,
}: ActiveBarProps) => {
  if (!x || !y || !width || !height) return <></>;

  const topBarValue =
    selectedKingdoms.length > 0
      ? Object.entries(payload).reduce((acc: number, [key, kingdomCount]) => {
          if (typeof kingdomCount !== 'number') return acc;

          if (!selectedKingdoms.includes(key as Kingdom)) return acc;

          return acc + kingdomCount;
        }, 0)
      : Object.values(payload).reduce((acc: number, curr) => (typeof curr === 'number' ? acc + curr : acc), 0);
  const isTopBar = value.at(-1) === topBarValue;

  const barMiddleX = (x as number) + (width as number) / 2;
  const barBottom = (y as number) + (height as number);
  const barTop = isTopBar ? background.y : y;

  const dotProps = {
    cx: barMiddleX,
    cy: y as number,
    fill: '#000000',
    strokeWidth: 0,
    opacity: 1,
  };

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={CHART_COLORS[dataKey as Kingdom]}
        stroke={SHARED_BAR_PROPS.stroke}
      />
      <line x1={barMiddleX} x2={barMiddleX} y1={barTop} y2={barBottom} stroke={colorPalette.divider} strokeWidth={1} />
      {isTopBar && (
        <Dot.Container {...dotProps}>
          <Dot.Active {...dotProps} fill='#000000' />
        </Dot.Container>
      )}
    </g>
  );
};

type CustomLegendBarProps = {
  kingdoms: Kingdom[];
  selectedKingdoms: Kingdom[];
  setSelectedKingdoms: (k: Kingdom[]) => void;
};

const CustomLegendBar = ({ kingdoms, selectedKingdoms, setSelectedKingdoms }: CustomLegendBarProps) => {
  const { t } = useTranslation();

  if (kingdoms.length === 1) {
    const kingdom = kingdoms[0] as Kingdom;

    const legendBarItems: ChartLegendBarItem[] = [
      {
        label: t(`global.kingdom.${kingdom}`),
        color: CHART_COLORS[`${kingdom}`],
        shape: 'column',
      },
    ];

    return (
      <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='threatened-species-chart-legend' />
    );
  }

  const handleLegendClick = (kingdom: Kingdom) => {
    let newSelectedKingdoms = selectedKingdoms.includes(kingdom)
      ? [...selectedKingdoms.filter((k) => k !== kingdom)]
      : [...selectedKingdoms, kingdom];

    if (newSelectedKingdoms.length === kingdoms.length) {
      newSelectedKingdoms = []; // Select "All" if all the groups are selected
    }

    setSelectedKingdoms(newSelectedKingdoms);
  };

  const legendBarItems: LegendItemProps[] = [
    {
      label: t('shared.ncaDetail.details.threatenedSpecies.graph.allKingdoms'),
      color: null,
      isActive: selectedKingdoms.length === 0,
      onClick: () => setSelectedKingdoms([]),
    },
    ...kingdoms.map((kingdom) => ({
      label: t(`global.kingdom.${kingdom}`),
      color: CHART_COLORS[`${kingdom}`],
      isActive: selectedKingdoms.includes(kingdom),
      onClick: () => handleLegendClick(kingdom),
    })),
  ];

  return (
    <Stack direction='row' centerMain spacing={2} data-testid='threatened-species-chart-legend'>
      {legendBarItems.map((item) => (
        <LegendButton key={item.label} {...item} />
      ))}
    </Stack>
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active || !payload) return null;

  const tooltipContent = payload.map((entry) => {
    return {
      label: t(`global.kingdom.${entry.name as Kingdom}`),
      value: entry.value,
    };
  });

  const totalValue = payload.reduce((acc, curr) => acc + (curr.value as number), 0);

  return (
    <ChartTooltip className='ml-1'>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{label}</ChartTooltipTextPrimary>
      </Stack>
      {tooltipContent.map((kingdomGroup, i) => {
        return (
          <div key={kingdomGroup.label}>
            <Stack direction='row' spacing={8} className='justify-between'>
              <ChartTooltipTextSecondary>{kingdomGroup.label}</ChartTooltipTextSecondary>
              <ChartTooltipTextSecondary>{kingdomGroup.value}</ChartTooltipTextSecondary>
            </Stack>
            {i < tooltipContent.length - 1 && <Divider className='mt-2' />}
          </div>
        );
      })}
      {tooltipContent.length > 1 && (
        <>
          <Divider />
          <Stack direction='row' spacing={8} className='justify-between'>
            <ChartTooltipTextPrimary>
              {t('shared.ncaDetail.details.threatenedSpecies.graph.total')}
            </ChartTooltipTextPrimary>
            <ChartTooltipTextPrimary>{totalValue}</ChartTooltipTextPrimary>
          </Stack>
        </>
      )}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipThreatenedSpecies };
